import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { isEqual } from 'lodash';

import { useMutation } from '@apollo/client';
import { Layout, Responsive as ReactGridLayout, WidthProvider } from 'react-grid-layout';
import { castDraft, produce } from 'immer';

import * as K from 'common/constants';
import { windowWidthState } from 'components/common/Managers/LayoutStateManager';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { showWaitCursor } from 'components/common/Page/Page';
import { getLrqStatus, LRQ_STATUS } from 'common/functions';
import { queryTimeRangeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import {
  DX_ROW,
  DY_ROW,
  getGraphsWithLayouts,
  getType,
  isLayoutFixed,
  isLayoutLocked,
  MIN_DX,
  trimLayout,
  trimLayouts,
} from '../dashboardData';
import { GET_CONFIGV2, SAVE_LAYOUT } from '../queries';
import { Dashboard, GraphConfigType } from '../types';
import { dashboardTabsState } from '../dashboards.state';
import DashboardGraph, { resizeHighcharts } from './DashboardGraph';
import { getGraphDataIds, LRQId } from './DashboardRequestManager';
import DashboardMarkdown from './DashboardMarkdown';
import DashboardChart from './DashboardChart';
import DashboardDistribution from './DashboardDistribution';
import DashboardHoneyComb from './DashboardHoneyComb';
import DashboardPowerQuery from './DashboardPowerQuery';
import EmptyDashboardTips from './EmptyDashboardTips';

import { hideNotification, showNotification } from './DashboardHeader';

import './DashboardGrid.scss';

type DashboardGridProps = {
  dashboard: Dashboard;
  idList: LRQId[];
  hasWriteAccess: boolean;
};

const SINGLE_COLUMN_WIDTH = 1000;
const GridLayout = WidthProvider(ReactGridLayout);

/**
 * This component displays the grid containing the content items for a dashboard. If there are no items, it will show
 * some sort of welcome / help info.
 *
 * @prop dashboard The configuration, layout and data for this dashboard
 */
function DashboardGrid({ dashboard, idList, hasWriteAccess }: DashboardGridProps): JSX.Element {
  const hasLayoutChanged = (layout1: Layout, layout2: Layout): boolean =>
    !isEqual(trimLayout(layout1), trimLayout(layout2));
  const haveLayoutsChanged = (layout1: Layout[], layout2: Layout[]): boolean =>
    !isEqual(trimLayouts(layout1), trimLayouts(layout2));

  const [prevLayout, setPrevLayout] = useState<Layout[] | undefined>(dashboard.layouts);
  const [currentLayout, setCurrentLayout] = useState<Layout[] | undefined>(dashboard.layouts);

  const windowWidth = useRecoilValue(windowWidthState);

  const activeTab = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
  const tabsList = useRecoilValue(dashboardTabsState);

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [saveLayout] = useMutation(SAVE_LAYOUT, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboard.config.name, startTime, endTime } }],
  });

  // This is a list of the Highcharts charts  so we can tell them to resize themselves when things change
  const [hiCharts, setHiCharts] = useState<Highcharts.Chart[]>([]);
  const { results, errors } = useEventCache([...idList.map((id) => id.id)]);

  useEffect(() => {
    hiCharts.forEach((chart) => {
      resizeHighcharts(chart);
    });
  }, [hiCharts, windowWidth]);

  // Reset the page's wait cursor if the state has changed
  const lrqStatus = getLrqStatus(results, errors, false);
  if (lrqStatus === LRQ_STATUS.DATA_READY || lrqStatus === LRQ_STATUS.HAS_ERRORS) {
    showWaitCursor(false);
  }

  // We save the latest layout only every n (currently .5) seconds to keep the browser from locking up
  const onLayoutMaybeChanged = (newLayout: Layout[]): void => {
    if (haveLayoutsChanged(currentLayout as Layout[], newLayout)) {
      setCurrentLayout(newLayout);
    }
  };

  // We only save the current layout when it looks like the user has stopped dragging
  useEffect(() => {
    if (haveLayoutsChanged(currentLayout as Layout[], prevLayout as Layout[])) {
      setPrevLayout(currentLayout);
      saveLayout({
        variables: {
          name: dashboard.config.name,
          graphs: JSON.stringify({
            graphs: getGraphsWithLayouts(dashboard.config.graphs as GraphConfigType[], currentLayout as Layout[]),
          }),
          tabName: dashboard.config.tabName,
        },
      });
    }
  }, [currentLayout, dashboard.config, saveLayout, prevLayout]);

  const afterChartCreated = (index: number, newChart: Highcharts.Chart): void => {
    setHiCharts(
      produce((draft) => {
        // Note: castDraft is no-op and only used to hint the type to typescript. See https://immerjs.github.io/immer/typescript/#cast-utilities
        draft[index] = castDraft(newChart);
      }),
    );
  };

  const isLayoutEditable =
    !isLayoutFixed(dashboard.config) && !isLayoutLocked(dashboard.config) && windowWidth > SINGLE_COLUMN_WIDTH;

  // Here is where we sort out what dashboards displayers we need and connect them to their LRQ data ids
  const configs = dashboard.config.graphs as GraphConfigType[];
  const keys = configs.map((_config, index) => `${index}`);
  const graphs = configs.map((config, index) => {
    let child;
    const dataIds = getGraphDataIds(idList, index);
    const vizType = getType(config);

    if (vizType.isMarkdown) {
      child = <DashboardMarkdown config={config} />;
    } else if (vizType.isDistribution) {
      child = <DashboardDistribution params={dashboard.params} index={index} config={config} dataIds={dataIds} />;
    } else if (vizType.isTimeseries || vizType.isPie || config.graphStyle === 'distribution') {
      child = (
        <DashboardChart
          params={dashboard.params}
          windowWidth={windowWidth}
          index={index}
          config={config}
          dataIds={dataIds}
          chartInstanceCallback={afterChartCreated}
        />
      );
    } else if (vizType.isHoneyComb) {
      child = (
        <DashboardHoneyComb
          config={config}
          dataIds={dataIds}
          params={dashboard.params}
          index={index}
          dashboardConfig={dashboard.config}
          current={currentLayout}
        />
      );
    } else {
      child = (
        <DashboardPowerQuery
          params={dashboard.params}
          index={index}
          config={config}
          dataIds={dataIds}
          dashboardConfig={dashboard.config}
        />
      );
    }
    return (
      <div key={keys[index]}>
        <DashboardGraph
          isDraggable={isLayoutEditable && hasWriteAccess}
          index={index}
          dashboardName={dashboard.config.name}
          title={config.title || config.label || 'Untitled'}
          config={config}
          dataIds={dataIds}
          params={dashboard.params}
          child={child}
          highChartInstance={hiCharts[index]}
          hasWriteAccess={hasWriteAccess}
        />
      </div>
    );
  });

  const onDragStop = (layouts: Layout[], preMovedLayout: Layout, movedLayout: Layout): void => {
    hideNotification();
    if (hasLayoutChanged(preMovedLayout, movedLayout)) {
      onLayoutMaybeChanged(layouts);
    }
  };

  const onResize = (_layout: Layout[], _prevLayout: Layout, resizedLayout: Layout): void => {
    if (_prevLayout.w !== resizedLayout.w) {
      let columnNote = '';
      for (let i = 5; i > -1; i -= 1) {
        if (resizedLayout.w === DX_ROW / i) {
          columnNote = ` (${i} columns)`;
          break;
        }
      }
      showNotification(`${(Number(resizedLayout.w / DX_ROW) * 100).toFixed(0)}% wide${columnNote}`);
    } else {
      hideNotification();
    }
    resizeHighcharts(hiCharts[Number(resizedLayout.i)]);
  };

  const onResizeStop = (resizedLayouts: Layout[], preResizeLayout: Layout, resizedLayout: Layout): void => {
    hideNotification();
    if (hasLayoutChanged(preResizeLayout, resizedLayout)) {
      onLayoutMaybeChanged(resizedLayouts);
    }
  };

  // No soup for you!
  if (dashboard.config.graphs.length === 0) {
    showWaitCursor(false);
    // when dashboard has tabs and active tab is not first tab show different template tip
    // this is controlled by isTabbed flag
    return <EmptyDashboardTips isTabbed={tabsList.indexOf(activeTab) > 0} />;
  }

  return (
    <>
      <GridLayout
        className="DashboardGrid"
        // This narrows the mouse events that the grid captures to just the graph title
        draggableHandle=".DashboardGraph__TitleBar"
        draggableCancel=".DashboardGraph__TitleBarIcons"
        // There are two other modes for how things get moved out of the way when dragging, but this one works best
        compactType="vertical"
        onDragStop={onDragStop}
        onResize={onResize}
        onResizeStop={onResizeStop}
        measureBeforeMount={false}
        useCSSTransforms={false}
        isDraggable={isLayoutEditable && hasWriteAccess}
        isResizable={isLayoutEditable && hasWriteAccess}
        // On displays narrower than SINGLE_COLUMN_WIDTH px across, we go to one column (i.e., MIN_DX grid column units)
        breakpoints={{ standard: SINGLE_COLUMN_WIDTH, singleColumn: 0 }}
        layouts={{ standard: dashboard.layouts as Layout[], singleColumn: dashboard.layouts as Layout[] }}
        // The number of logical columns across by breakpoint
        cols={{ standard: DX_ROW, singleColumn: MIN_DX }}
        resizeHandles={['s', 'e', 'se']}
        // How tall each row unit is
        rowHeight={DY_ROW}
        // These are hard-coded instead of supplied via css so they can calc the full layout
        margin={[20, 24]}
        containerPadding={[20, 0]}
      >
        {graphs}
      </GridLayout>
    </>
  );
}

export default DashboardGrid;
